import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';


const MobileBottomNav = ({ 
  userRole = 'technician',
  activeOrders = 2,
  isVisible = true 
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [workingTime, setWorkingTime] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      if (isTimerActive) {
        setWorkingTime(prev => prev + 1);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isTimerActive]);

  // Only show for technician role on mobile devices
  if (userRole !== 'technician' || !isVisible) {
    return null;
  }

  const navigationItems = [
    {
      key: 'orders',
      label: 'Ordens',
      icon: 'ClipboardList',
      path: '/service-orders',
      badge: activeOrders > 0 ? activeOrders : null
    },
    {
      key: 'mobile',
      label: 'Mobile',
      icon: 'Smartphone',
      path: '/technician-mobile',
      badge: null
    },
    {
      key: 'timer',
      label: isTimerActive ? 'Pausar' : 'Iniciar',
      icon: isTimerActive ? 'Pause' : 'Play',
      action: 'timer',
      badge: null
    },
    {
      key: 'emergency',
      label: 'SOS',
      icon: 'AlertTriangle',
      action: 'emergency',
      badge: null,
      variant: 'destructive'
    }
  ];

  const handleNavigation = (item) => {
    if (item?.action === 'timer') {
      setIsTimerActive(!isTimerActive);
      // Add haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    } else if (item?.action === 'emergency') {
      handleEmergency();
    } else if (item?.path) {
      navigate(item?.path);
    }
  };

  const handleEmergency = () => {
    // Emergency action - could trigger call, send location, etc.
    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200]);
    }
    
    // Show emergency modal or make emergency call
    const confirmed = window.confirm(
      'Deseja acionar o protocolo de emergência?\n\nIsso irá notificar a central e enviar sua localização.'
    );
    
    if (confirmed) {
      // Implement emergency protocol
      console.log('Emergency protocol activated');
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours?.toString()?.padStart(2, '0')}:${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
    }
    return `${minutes?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const isCurrentPath = (path) => {
    return location?.pathname === path;
  };

  return (
    <>
      {/* Timer Display Bar */}
      {isTimerActive && (
        <div className="fixed bottom-20 left-0 right-0 z-80 bg-success text-success-foreground px-4 py-2 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-success-foreground rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">Tempo ativo</span>
            </div>
            <div className="font-mono text-sm font-semibold">
              {formatTime(workingTime)}
            </div>
          </div>
        </div>
      )}
      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-90 bg-surface border-t border-border shadow-lg lg:hidden">
        <div className="grid grid-cols-4 h-16">
          {navigationItems?.map((item) => {
            const isActive = item?.path && isCurrentPath(item?.path);
            const isTimerItem = item?.action === 'timer';
            const isEmergencyItem = item?.action === 'emergency';
            
            return (
              <button
                key={item?.key}
                onClick={() => handleNavigation(item)}
                className={`relative flex flex-col items-center justify-center space-y-1 touch-target transition-colors ${
                  isActive 
                    ? 'text-primary bg-primary/5' 
                    : isTimerItem && isTimerActive
                    ? 'text-success bg-success/5'
                    : isEmergencyItem
                    ? 'text-error hover:bg-error/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                {/* Badge */}
                {item?.badge && (
                  <span className="absolute top-1 right-1/4 w-5 h-5 bg-error text-error-foreground text-xs rounded-full flex items-center justify-center font-medium">
                    {item?.badge > 99 ? '99+' : item?.badge}
                  </span>
                )}
                {/* Icon */}
                <Icon 
                  name={item?.icon} 
                  size={20} 
                  className={
                    isTimerItem && isTimerActive 
                      ? 'text-success' 
                      : isEmergencyItem 
                      ? 'text-error' 
                      : undefined
                  }
                />
                {/* Label */}
                <span className="text-xs font-medium leading-none">
                  {item?.label}
                </span>
                {/* Active Indicator */}
                {isActive && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full"></div>
                )}
                {/* Timer Active Indicator */}
                {isTimerItem && isTimerActive && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-success rounded-full"></div>
                )}
              </button>
            );
          })}
        </div>
      </nav>
      {/* Spacer for fixed bottom navigation */}
      <div className="h-16 lg:hidden"></div>
    </>
  );
};

export default MobileBottomNav;